import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { authAtom } from '../store/auth';
import {
  clearReadNotifications,
  deleteNotification,
  fetchNotificationPreferences,
  fetchNotifications,
  fetchUnreadCount,
  markAllNotificationsRead,
  markNotificationRead,
  markNotificationUnread,
  sendTestNotification,
  updateNotificationPreferences,
} from '../api/notificationsApi';

export const NOTIFICATIONS_KEY = 'notifications';

export const notificationKeys = {
  all: [NOTIFICATIONS_KEY],
  lists: () => [NOTIFICATIONS_KEY, 'list'],
  list: (params) => [NOTIFICATIONS_KEY, 'list', params],
  unread: () => [NOTIFICATIONS_KEY, 'unread-count'],
  preferences: () => [NOTIFICATIONS_KEY, 'preferences'],
};

const useIsAuthenticated = () => Boolean(useAtomValue(authAtom)?.access_token);

export const useNotifications = (params = {}, { enabled = true } = {}) => {
  const authenticated = useIsAuthenticated();
  return useQuery({
    queryKey: notificationKeys.list(params),
    queryFn: () => fetchNotifications(params),
    enabled: authenticated && enabled,
    placeholderData: keepPreviousData,
    staleTime: 15_000,
    retry: 1,
  });
};

export const useUnreadNotificationCount = () => {
  const authenticated = useIsAuthenticated();
  return useQuery({
    queryKey: notificationKeys.unread(),
    queryFn: async () => (await fetchUnreadCount())?.unread_count ?? 0,
    enabled: authenticated,
    refetchInterval: 60_000,
    staleTime: 30_000,
    retry: 1,
  });
};

const patchListItems = (queryClient, update) => {
  queryClient.setQueriesData({ queryKey: notificationKeys.lists() }, (page) => {
    if (!page?.notifications) return page;
    return { ...page, notifications: update(page.notifications) };
  });
};

export const useNotificationActions = () => {
  const queryClient = useQueryClient();

  const settle = (result) => {
    if (typeof result?.unread_count === 'number') {
      queryClient.setQueryData(notificationKeys.unread(), result.unread_count);
    }
    queryClient.invalidateQueries({ queryKey: notificationKeys.lists() });
  };

  const optimisticRead = (read) => (id) => {
    patchListItems(queryClient, (items) =>
      items.map((item) =>
        String(item.id) === String(id)
          ? { ...item, read, read_at: read ? new Date().toISOString() : null }
          : item
      )
    );
  };

  const onError = () => {
    queryClient.invalidateQueries({ queryKey: notificationKeys.all });
  };

  const markRead = useMutation({
    mutationFn: markNotificationRead,
    onMutate: optimisticRead(true),
    onSuccess: settle,
    onError,
  });

  const markUnread = useMutation({
    mutationFn: markNotificationUnread,
    onMutate: optimisticRead(false),
    onSuccess: settle,
    onError,
  });

  const markAllRead = useMutation({
    mutationFn: markAllNotificationsRead,
    onSuccess: settle,
    onError,
  });

  const remove = useMutation({
    mutationFn: deleteNotification,
    onMutate: (id) =>
      patchListItems(queryClient, (items) =>
        items.filter((item) => String(item.id) !== String(id))
      ),
    onSuccess: settle,
    onError,
  });

  const clearRead = useMutation({
    mutationFn: clearReadNotifications,
    onSuccess: settle,
    onError,
  });

  return { markRead, markUnread, markAllRead, remove, clearRead };
};

export const useNotificationPreferences = () => {
  const authenticated = useIsAuthenticated();
  return useQuery({
    queryKey: notificationKeys.preferences(),
    queryFn: fetchNotificationPreferences,
    enabled: authenticated,
    staleTime: 60_000,
    retry: 1,
  });
};

export const useUpdateNotificationPreferences = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateNotificationPreferences,
    onSuccess: (data) => {
      queryClient.setQueryData(notificationKeys.preferences(), data);
    },
  });
};

export const useSendTestNotification = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: sendTestNotification,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.lists() });
      queryClient.invalidateQueries({ queryKey: notificationKeys.unread() });
    },
  });
};
